import CloseSVG from '@public/icons/close-btn.svg';
import { useAnimation } from 'framer-motion';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Popup from '@/shared/components/shared/Popup';
import Typografed from '@/shared/components/shared/Typografed';
import { useMounted } from '@/shared/hooks/use-mounted';
import { easeInOutSine } from '@/shared/lib/utils/easings';
import { FoodAreasListProps } from '../../sections/FoodAreasList/FoodAreasList';
import Image from '../../shared/Image';
import Pagination from '../../shared/Pagination/Pagination';
import Responsive from '../../shared/Responsive';
import { SCHEMA_POPUP_NAME } from '../SchemaPopup/SchemaPopup';

export const FOOD_AREA_POPUP_NAME = 'food-area-popup';

function FoodAreaPopup({ foodArea }: { foodArea: FoodAreasListProps | null }) {
    const { openPopup, closePopup } = useSetOpenedPopupsState();
    const [lastRealIndex, setLastRealIndex] = useState(0);
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const numSlides = foodArea?.images?.length ?? 0;
    const [direction, setDirection] = useState(0);
    const controls = useAnimation();
    const device = useMediaQueryDeviceStateValue();
    const isMobile = device.includes('mobile');
    const isMounted = useMounted();
    const sliderRef = useRef<SwiperRef>();

    useEffect(() => {
        if (currentRealIndex === 0 && lastRealIndex === numSlides - 1) {
            setDirection(1);
        } else if (currentRealIndex === numSlides - 1 && lastRealIndex === 0) {
            setDirection(-1);
        } else if (currentRealIndex > lastRealIndex) {
            setDirection(1);
        } else if (currentRealIndex < lastRealIndex) {
            setDirection(-1);
        }
        controls.start({
            x: direction > 0 ? [0, 30, 0] : [0, -30, 0],
            scale: [1.1, 1.1],
            transition: {
                duration: 0.9,
                ease: easeInOutSine,
                delay: 0.1,
                stiffness: 0,
                damping: 0,
            },
        });
    }, [currentRealIndex, lastRealIndex, numSlides, direction, controls]);

    return (
        <Popup name={FOOD_AREA_POPUP_NAME} data-popup-preset="slide-right" className="food-area-popup" overlay={true}>
            <button
                className="menu-popup-close-btn"
                onClick={() => {
                    closePopup(FOOD_AREA_POPUP_NAME);
                }}
                aria-label="Закрыть окно"
            >
                <CloseSVG />
            </button>
            <div className="food-area-popup-content">
                {foodArea?.name && (
                    <div className="food-area-popup-content__title text-xl">
                        <Typografed>{foodArea.name}</Typografed>
                    </div>
                )}
                <div className="food-area-popup-content__info">
                    {foodArea?.access && (
                        <div className="food-area-popup-content__info-item">
                            <p className="food-area-popup-content__info-label text-xs">Доступ:</p>
                            <p className="food-area-popup-content__info-value text-s">
                                <Typografed>{foodArea.access}</Typografed>
                            </p>
                        </div>
                    )}
                    {foodArea?.format && (
                        <div className="food-area-popup-content__info-item">
                            <p className="food-area-popup-content__info-label text-xs">Формат:</p>
                            <p className="food-area-popup-content__info-value text-s">
                                <Typografed>{foodArea.format}</Typografed>
                            </p>
                        </div>
                    )}
                    {foodArea?.kitchen && (
                        <div className="food-area-popup-content__info-item">
                            <p className="food-area-popup-content__info-label text-xs">Кухня:</p>
                            <p className="food-area-popup-content__info-value text-s">
                                <Typografed>{foodArea.kitchen}</Typografed>
                            </p>
                        </div>
                    )}
                    {foodArea?.operating_mode && (
                        <div className="food-area-popup-content__info-item">
                            <p className="food-area-popup-content__info-label text-xs">Режим работы:</p>
                            <div className="food-area-popup-content__info-value food-area-popup-content__info-date text-s">
                                {foodArea?.operating_mode.map((info) => (
                                    <div key={info.days}>
                                        <p>{info.days}</p>
                                        <p>{info.time}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {foodArea?.place && (
                        <div className="food-area-popup-content__info-item">
                            <p className="food-area-popup-content__info-label text-xs">Место:</p>
                            <div
                                role="button"
                                tabIndex={0}
                                className={`food-area-popup-content__info-value food-area-popup-content__info-schema text-s ${
                                    foodArea?.place?.image ? '' : 'food-area-popup-content__info-value--disabled'
                                }`}
                                onClick={() => {
                                    openPopup(SCHEMA_POPUP_NAME);
                                }}
                                aria-label="Открыть схему"
                            >
                                {foodArea?.place?.name && <Typografed>{foodArea.place.name}</Typografed>}
                            </div>
                        </div>
                    )}
                </div>

                {foodArea?.description && (
                    <div
                        className="food-area-popup-content__description text-s"
                        dangerouslySetInnerHTML={{ __html: foodArea.description }}
                    />
                )}
                {foodArea?.images?.length ? (
                    <div className="food-area-popup-content__slider">
                        {numSlides > 1 && isMounted ? (
                            <div className="food-area-popup-content__slider-wrapper">
                                <Swiper
                                    key={foodArea.id}
                                    className="how-it-was-slider"
                                    modules={[Navigation]}
                                    navigation={{
                                        prevEl: '.slider-prev-btn',
                                        nextEl: '.slider-next-btn',
                                    }}
                                    ref={sliderRef as MutableRefObject<SwiperRef>}
                                    speed={650}
                                    slidesPerView={1}
                                    spaceBetween={20}
                                    centeredSlides={isMobile}
                                    onSlideChange={(swiper) => {
                                        const newRealIndex = swiper.realIndex;
                                        setLastRealIndex(currentRealIndex);
                                        setCurrentRealIndex(newRealIndex);
                                    }}
                                    onResize={(swiper) => swiper.update()}
                                >
                                    {foodArea?.images.map((slide, i) => (
                                        <SwiperSlide className="food-area-popup-content__slide" key={i}>
                                            <Responsive className="how-it-was-slider-img">
                                                <div className="how-it-was-slider-img-inner">
                                                    <Image
                                                        className="responsive__item"
                                                        src={slide.src}
                                                        alt={slide.alt || ''}
                                                        title={slide.title}
                                                        width={slide.width}
                                                        height={slide.height}
                                                        sizes="(max-width: 1199px) 20vw, 30vw"
                                                    />
                                                </div>
                                            </Responsive>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <Pagination
                                    numSlides={numSlides}
                                    currentRealIndex={currentRealIndex}
                                    lastRealIndex={lastRealIndex}
                                    prevElClass="slider-prev-btn"
                                    nextElClass="slider-next-btn"
                                    wrapperClass="reviews-pagination-wrapper wrapper"
                                />
                            </div>
                        ) : (
                            <div className="wrapper swiper__wrapper">
                                {foodArea?.images.map((slide, i) => (
                                    <div className="swiper-slide reviews-slider-slide" key={i}>
                                        <div className="reviews-slider-slide-inner">
                                            <Responsive className="reviews-slider-img-wrapper">
                                                <Image
                                                    className="reviews-slider-img"
                                                    src={slide.src}
                                                    alt={slide.alt || ''}
                                                    title={slide.title}
                                                    width={slide.width}
                                                    height={slide.height}
                                                    sizes="(max-width: 1199px) 20vw, 30vw"
                                                />
                                            </Responsive>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </Popup>
    );
}

export default FoodAreaPopup;
